import {FormControl, FormHelperText, Theme} from "@mui/material";
import classNames from "classnames";
import classnames from "classnames";
import {Field} from "react-final-form";
import React, {cloneElement, FunctionComponent, useCallback, useMemo} from "react";
import {MuiRFF} from "../../form";
import UIUtils from "../../../utils";
import Utils from "../../../../core/services/utils/utils";
import NewDropZone from "../../base/new-drop-zone";
import useUploadState from "../../../hooks/use-upload-state";
import useIsMounted from "../../../hooks/use-is-mounted";
// @ts-ignore
import {ReactComponent as Placeholder} from "../../../../assets/images/dropzone/upload-placeholder.svg";
import Removable from "../../app-specific/removable";
import {FileUtilsInitialFileUploadArg} from "../../../../core/services/file-utils";
import {BizFormApiClient} from "../../../../packages/biz-form";

type DropzoneFieldProps = Omit<MuiRFF.TextFieldProps, 'onChange' | 'onBlur' | 'value' | 'type'> & {
    form?: any,
    apiClient?: BizFormApiClient,
    onChange?: (value?: string | File) => void,
    onUploadStart?: () => void,
    onUploadEnd?: () => void,
    getFullFileUrl?: (fileGuid: string) => string,
    initialFileUpload: (arg: FileUtilsInitialFileUploadArg) => Promise<any>,
    uploadFile: (file: File, initialFileUpload: (arg: FileUtilsInitialFileUploadArg) => Promise<any>, getFileGuid: (data: any) => string, onProgress: (progress: number) => void, apiClient?: BizFormApiClient) => Promise<any | Error>,
    remove?: (fileGuid: string | File) => Promise<boolean | number>,
    getFileGuid: (data: any) => string,
    getFileExtension: (data: any) => string,
    accept?: string[],
    useFileAsValue?: boolean;
    disableAutoFormChange?: boolean;
}

const DropzoneField: FunctionComponent<DropzoneFieldProps> = (props) => {
    const id = useMemo(() => props.id || props.name, [props.id, props.name]);
    const {onChange, onUploadStart, remove, onUploadEnd, initialFileUpload, getFileGuid, uploadFile} = props;

    const [uploadState, setUploadState, initialUploadState] = useUploadState();
    const isMounted = useIsMounted();

    /**
     * The form control sx.
     */
    const formControlSx = useCallback<ThemeMixin<Theme>>((theme) => ({
        width: '100%',
        ...UIUtils.invokeSx(props.sx, theme) as any,
    }), [props.sx])

    /**
     * Set the files.
     */
    const setFiles = useCallback(async (files: File[]) => {
        const file = files[0];
        if (!file) {
            return;
        }
        if (props.useFileAsValue) {
            if (!props.disableAutoFormChange) {
                props.form?.change(props.name, file);
            }
            onChange?.(file)
            return;
        }
        if (onUploadStart) {
            onUploadStart();
        }
        const infoOrError = await uploadFile?.(
            file,
            initialFileUpload,
            getFileGuid,
            (progress) => {
                if (!isMounted())
                    return;
                setUploadState(prevState => ({
                    ...prevState,
                    uploading: true,
                    done: false,
                    progress: progress,
                    error: undefined,
                }));
            },
            props.apiClient,
        )
        if (!isMounted())
            return;
        if (onUploadEnd) {
            onUploadEnd();
        }
        if (infoOrError instanceof Error) {
            setUploadState(prevState => ({
                ...prevState,
                uploading: false,
                done: true,
                progress: 100,
                error: infoOrError.message,
                tempUrl: undefined,
            }));
            setTimeout(() => setUploadState(initialUploadState), 5000);
        } else {
            const fileGuid = getFileGuid(infoOrError);
            const fileExtension = props.getFileExtension?.(infoOrError);
            const fileUrl = `${fileGuid}${fileExtension ? `.${fileExtension}` : ''}`;
            if (!props.disableAutoFormChange) {
                props.form?.change(props.name, fileUrl);
            }
            onChange?.(fileUrl)
            setUploadState(initialUploadState)
        }
    }, [props, onUploadStart, uploadFile, initialFileUpload, getFileGuid, isMounted, onUploadEnd, onChange, setUploadState, initialUploadState])

    /**
     * Remove the file.
     */
    const removeFile = useCallback(async (value: string | File) => {
        const valueIsFile = value instanceof File;
        const fileName = valueIsFile ? (value as File).name : value as string
        if (onUploadStart) {
            onUploadStart();
        }
        const resultFlagOrErrorCode = await remove?.(props.useFileAsValue ? value : fileName.split('.')[0]);
        if (!isMounted())
            return;
        if (onUploadEnd) {
            onUploadEnd();
        }
        // -605 says file not found meaning that the file must have already been deleted.
        if (resultFlagOrErrorCode === true || resultFlagOrErrorCode === -605) {
            if (!props.disableAutoFormChange) {
                props.form?.change(props.name, undefined)
            }
            onChange?.(undefined)
        }
    }, [isMounted, onChange, onUploadEnd, onUploadStart, props.disableAutoFormChange, props.form, props.name, props.useFileAsValue, remove])


    return (
        <Field
            name={props.name}
            render={({input, meta}) => {
                const {error, submitError} = meta;
                const isError = MuiRFF.showErrorOnChange({meta}) || !!uploadState.error;
                return (
                    <FormControl
                        sx={formControlSx}
                        className={classNames({'error': isError}, 'operator-profile-image')}
                        error={isError}
                        required={props.required}
                        disabled={props.disabled || uploadState.uploading}
                    >
                        {/*@ts-ignore*/}
                        <NewDropZone
                            setFiles={setFiles}
                            loading={false}
                            id={id}
                            noParse
                            dropZoneClassName={'drop-zone'}
                            {...(!!props.accept && {accept: props.accept})}
                            uploading={uploadState.uploading}
                            openOnClick={!(uploadState.uploading || props.disabled)}
                            disabled={uploadState.uploading || props.disabled}
                            multiple={false}
                            notAcceptedDurationInMs={2000}
                            error={isError}
                            endDragging={() => {
                            }}
                        >
                            {(_, highlight, notAccepted) => {
                                const valueIsFile = input.value instanceof File;
                                return (
                                    <>
                                        <div className={classnames('current-operator-image')}>
                                            {
                                                uploadState.uploading &&
                                                <div className={'loading operator-profile-image-uploader'}>
                                                    <div/>
                                                </div>
                                            }
                                            {
                                                !uploadState.uploading &&
                                                <>
                                                    {
                                                        !input.value &&
                                                        <Placeholder/>
                                                    }
                                                    {
                                                        input.value &&
                                                        <>
                                                            {
                                                                !!remove && !props.disabled &&
                                                                <Removable onRemove={(e: any) => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    void removeFile(input.value)
                                                                }} className={''}>
                                                                    {
                                                                        (valueIsFile && (input.value as File)?.type?.includes('image')) ||
                                                                        (!valueIsFile && UIUtils.isImage(input.value?.split('.').pop().split('?')[0]))
                                                                            ? <img
                                                                                src={valueIsFile ? URL.createObjectURL(input.value) : Utils.encodeFileUrl(props.getFullFileUrl?.(input.value)) as string}
                                                                                alt={'file'}
                                                                                style={{
                                                                                    width: 200,
                                                                                    maxHeight: 200,
                                                                                    marginInline: 'auto',
                                                                                    display: 'flex'
                                                                                }}
                                                                            />
                                                                            : cloneElement(
                                                                                UIUtils.getFileIcon((valueIsFile ? input.value.name : input.value)?.split('.').pop().split('?')[0], false,
                                                                                    <Placeholder/>),
                                                                                {
                                                                                    style: {
                                                                                        width: 100,
                                                                                        height: 100,
                                                                                        marginInline: 'auto',
                                                                                        display: 'flex'
                                                                                    }
                                                                                }
                                                                            )
                                                                    }
                                                                </Removable>
                                                            }
                                                            {
                                                                !(!!remove && !props.disabled) && (
                                                                    (valueIsFile && (input.value as File)?.type?.includes('image')) ||
                                                                    (!valueIsFile && UIUtils.isImage(input.value?.split('.').pop().split('?')[0]))
                                                                        ? <img
                                                                            src={valueIsFile ? URL.createObjectURL(input.value) : Utils.encodeFileUrl(props.getFullFileUrl?.(input.value)) as string}
                                                                            alt={'file'}
                                                                            style={{width: 200, maxHeight: 200}}
                                                                        />
                                                                        : cloneElement(
                                                                            UIUtils.getFileIcon((valueIsFile ? input.value.name : input.value)?.split('.').pop().split('?')[0], false,
                                                                                <Placeholder/>),
                                                                            {
                                                                                style: {width: 100, height: 100}
                                                                            }
                                                                        )
                                                                )
                                                            }
                                                        </>
                                                    }
                                                </>
                                            }
                                            <div
                                                className={classnames('text-center text-sm font-weight-semi-bold call-to-action mt-2', {'not-accepted': notAccepted, 'error': isError})}>
                                                {
                                                    uploadState.error
                                                        ? uploadState.error
                                                        : notAccepted
                                                            ? "This format is not supported"
                                                            : uploadState.uploading
                                                                ? ""
                                                                : `Drag & Drop a file (${props.accept?.join(', ') ?? "All formats"}) or click to select`
                                                }
                                            </div>
                                            {
                                                (isError || !!props.helperText) &&
                                                <FormHelperText error={isError}>
                                                    {error || submitError || props.helperText}
                                                </FormHelperText>
                                            }
                                        </div>
                                    </>
                                )
                            }}
                        </NewDropZone>
                    </FormControl>
                )
            }}
        />
    );
}

export default DropzoneField;
